import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SaleDepositReportTable from "../components/SaleDepositReport/Table";
import { saleDepositSummaryUrl } from "../components/SaleDepositReport/depositReportUrl";
import {
  ClosingBranchDay,
  formatBusinessDate,
} from "../components/SaleDepositReport/saleCenterClosing.utils";

const API_DATE_FORMAT = "YYYY-MM-DD";

/**
 * A path param is user-typed until proven otherwise, and a bad day would be handed straight
 * to the ปิดศูนย์เซล panel. Round-tripping through the format is what rejects "2026-13-40"
 * as well as a day dayjs would silently reinterpret.
 */
const isBusinessDate = (value: string): boolean => {
  const parsed = dayjs(value);
  return parsed.isValid() && parsed.format(API_DATE_FORMAT) === value;
};

/**
 * One branch-day of รายงานการโอน — the routes behind a row of the branch-day summary, plus
 * the ปิดศูนย์เซล panel for that day.
 *
 * The branch and the day come from the path, so the panel's "one branch, one day"
 * precondition holds by construction and it is always on here. Everything after the path is
 * the summary's own query string, carried along untouched so ย้อนกลับ can put the supervisor
 * back on the day and filters they came from — the customer's whole point in asking for the
 * drill-down.
 */
export default function SaleDepositBranchDayPage() {
  const { branchCode, businessDate } = useParams<{
    branchCode: string;
    businessDate: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const summarySearch = searchParams.toString();

  const branchDay = useMemo<ClosingBranchDay | null>(
    () =>
      branchCode && businessDate && isBusinessDate(businessDate)
        ? { branchCode, businessDate }
        : null,
    [branchCode, businessDate],
  );

  const goBackToSummary = () => {
    navigate(saleDepositSummaryUrl(summarySearch));
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Button
          color="inherit"
          startIcon={<ArrowBackIosNewIcon fontSize="small" />}
          onClick={goBackToSummary}
        >
          ย้อนกลับ
        </Button>
        <Typography variant="h6">
          {branchDay
            ? `รายงานการโอน · สาขา ${branchDay.branchCode} · วันที่ ${formatBusinessDate(
                branchDay.businessDate,
              )}`
            : "รายงานการโอน"}
        </Typography>
      </Stack>
      {branchDay ? (
        // Keyed by the branch-day: the table seeds its filters once, so a different
        // branch-day has to start a fresh one rather than keep the previous day's.
        <SaleDepositReportTable
          key={`${branchDay.branchCode}-${branchDay.businessDate}`}
          branchDay={branchDay}
        />
      ) : (
        <Typography color="text.secondary" fontWeight={500}>
          ลิงก์ไม่ถูกต้อง กรุณากลับไปเลือกสาขาจากรายงานการโอน
        </Typography>
      )}
    </Box>
  );
}
